import { useMemo, useState } from "react";
import { recordShopifyFeeEventApi } from "../../../util/api";
import css from "./ShopifyVarintsForm.module.css";

const priceToCents = (value, fallbackAmount) => {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.round(parsed * 100);
  }
  return Number(fallbackAmount) || 0;
};

const getBuyerDisplayName = currentUser => {
  const profile = currentUser?.attributes?.profile;
  return profile?.displayName || profile?.publicData?.displayName || '';
};

const getSellerDisplayName = (author, listing) => {
  const profile = author?.attributes?.profile || listing?.author?.attributes?.profile;
  return profile?.displayName || profile?.publicData?.displayName || profile?.publicData?.storeName || '';
};

export default function ShopifyVarintsForm({ publicData, listing, author, currentUser, price }) {

  const { shopifyVariants: variants, shopifyProductQuantity } = publicData || {};

  const [selectedOptions, setSelectedOptions] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [checkoutInProgress, setCheckoutInProgress] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  const hasVariants = Array.isArray(variants) && variants.length > 0;
  const stockQuantity = Number(shopifyProductQuantity) || 0;

  const groupedVariants = useMemo(() => {
    if (!hasVariants) return [];

    return Object.values(
      variants.reduce((acc, item) => {
        if (!item?.id || !item?.option) return acc;

        if (!acc[item.id]) {
          acc[item.id] = {
            id: item.id,
            price: item.price || 0,
            quantity: item.quantity || 0,
            options: {},
          };
        }

        acc[item.id].options[item.option] = item.label;
        return acc;
      }, {})
    );
  }, [variants, hasVariants]);

  const optionGroups = useMemo(() => {
    const groups = {};

    groupedVariants.forEach((variant) => {
      Object.entries(variant.options || {}).forEach(([name, value]) => {
        if (!groups[name]) {
          groups[name] = new Set();
        }

        groups[name].add(value);
      });
    });

    return groups;
  }, [groupedVariants]);

  const matchedVariant = useMemo(() => {
    if (!groupedVariants.length) return null;
    if (Object.keys(selectedOptions).length !== Object.keys(optionGroups).length) return null;

    return groupedVariants.find((variant) =>
      Object.entries(selectedOptions).every(
        ([key, value]) => variant.options?.[key] === value
      )
    );
  }, [groupedVariants, selectedOptions, optionGroups]);

  const handleSelect = (optionName, value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [optionName]: value,
    }));
  };

  const handleBuyNow = async selectedVariant => {
    if (checkoutInProgress) return;

    const listingId = listing?.id?.uuid;
    const sellerId = author?.id?.uuid || listing?.author?.id?.uuid;
    if (!listingId || !sellerId) {
      setCheckoutError('Unable to start Shopify checkout for this listing.');
      return;
    }

    setCheckoutInProgress(true);
    setCheckoutError(null);

    try {
      const res = await recordShopifyFeeEventApi({
        listingId,
        sellerId,
        sellerIdentifier: getSellerDisplayName(author, listing),
        buyerId: currentUser?.id?.uuid,
        buyerDisplayName: getBuyerDisplayName(currentUser),
        buyerEmail: currentUser?.attributes?.email,
        itemName: listing?.attributes?.title,
        quantity,
        unitPriceCents: priceToCents(selectedVariant?.price, price?.amount),
        variantId: selectedVariant?.id,
      });

      if (res?.checkoutUrl) {
        window.location.href = res.checkoutUrl;
      } else {
        throw new Error('Shopify checkout URL was not returned.');
      }
    } catch (error) {
      setCheckoutError(error?.message || 'Unable to start Shopify checkout.');
      setCheckoutInProgress(false);
    }
  };

  if (stockQuantity === 0 && !hasVariants) {
    return (
      <p className={css.outStock}>
        Out of stock
      </p>
    );
  }

  if (!hasVariants) {
    return (
      <div className={css.productVariants}>
        <div className={css.variantInfo}>
          <p>
            <strong>Stock:</strong> {stockQuantity}
          </p>
        </div>
<div>
  <div className={css.variantTitle}>
    Quantity:
  </div>
        <div className={css.quantityWrapper}>
          <button
            className={css.qtyBtn}
            onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
            disabled={quantity <= 1}
          >
            -
          </button>

          <div className={css.qtyValue}>{quantity}</div>

          <button
            className={css.qtyBtn}
            onClick={() => setQuantity((prev) => Math.min(stockQuantity, prev + 1))}
            disabled={quantity >= stockQuantity}
          >
            +
          </button>
        </div>
</div>

        <div className={css.actionButtons}>
          <button
            className={css.buyBtn}
            disabled={stockQuantity <= 0 || checkoutInProgress}
            onClick={() => handleBuyNow(null)}
          >
            {checkoutInProgress ? 'Opening Shopify...' : 'Buy Now'}
          </button>
        </div>
        {checkoutError ? <p className={css.errorText}>{checkoutError}</p> : null}
      </div>
    );
  }

  return (
    <div className={css.productVariants}>
      {/* Dynamic Options */}
      {Object.entries(optionGroups).map(
        ([optionName, values]) => (
          <div
            className={css.variantSection}
            key={optionName}
          >
            <h3 className={css.variantTitle}>
              {optionName}:
            </h3>

            <div
              className={css.variantOptions}
            >
              {[...values].map((value) => {
                const active =
                  selectedOptions[
                  optionName
                  ] === value;

                return (
                  <button
                    key={value}
                    onClick={() =>
                      handleSelect(
                        optionName,
                        value
                      )
                    }
                    className={`${css.variantBtn} ${active
                        ? css.active
                        : ""
                      }`}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
        )
      )}

      {/* No Selection */}
      {!Object.keys(selectedOptions).length && (
        <p className={css.infoText}>
          Please select product options
        </p>
      )}

      {/* Variant Found */}
      {matchedVariant ? (
        <>
          <div className={css.variantInfo}>
            <p>
              <strong>Price:</strong> ${matchedVariant.price}
            </p>

            <p>
              <strong>Stock:</strong> {matchedVariant.quantity}
            </p>

            <p>
              <strong>ID:</strong> {matchedVariant.id}
            </p>

            {matchedVariant.quantity <= 0 && (
              <p className={css.outStock}>
                Out of stock
              </p>
            )}
          </div>

          {/* Quantity */}
          <div  className={css.variantSection}>
            <div className={css.variantTitle}>
              Quantity :
            </div>
          <div
            className={css.quantityWrapper}
          >
            <button
              className={css.qtyBtn}
              onClick={() =>
                setQuantity((prev) =>
                  Math.max(1, prev - 1)
                )
              }
              disabled={quantity <= 1}
            >
              -
            </button>

            <div className={css.qtyValue}>
              {quantity}
            </div>

            <button
              className={css.qtyBtn}
              onClick={() =>
                setQuantity((prev) =>
                  Math.min(
                    Math.max(0, matchedVariant?.quantity || 0),
                    prev + 1
                  )
                )
              }
              disabled={quantity >= (matchedVariant?.quantity || 0)}
            >
              +
            </button>
          </div>
          </div>

          {/* Buttons */}
          <div
            className={css.actionButtons}
          >
            <button
              className={css.buyBtn}
              disabled={matchedVariant?.quantity <= 0 || checkoutInProgress}
              onClick={() => handleBuyNow(matchedVariant)}
            >
              {checkoutInProgress ? 'Opening Shopify...' : 'Buy Now'}
            </button>
          </div>
          {checkoutError ? <p className={css.errorText}>{checkoutError}</p> : null}
        </>
      ) : (
        Object.keys(selectedOptions).length >
        0 && (
          <p className={css.notFound}>
            Variant not found
          </p>
        )
      )}
    </div>
  );
}
